import type { LatencyRange } from '../../lib/benchmarks';
import { latencyLabels, latencyTrackX, LATENCY_TRACK } from '../../lib/benchmarks';
import { fmtCallSample, fmtSec } from '../../lib/format';
import { m } from '../../paraglide/messages';

export interface LatencyRangeChartProps {
  range: LatencyRange;
  /** successful calls behind the percentiles; absent on the old contract */
  sample?: number;
  /** multiple endpoint percentiles were combined without raw observations */
  estimated?: boolean;
}

/* SVG geometry: 0s sits at x=8, the scale ceiling at x=552 (544px of track). */
const { x0: X0, width: TRACK } = LATENCY_TRACK;

/** The chart heading, with the call sample behind it when the gateway sends one. */
const latencyTitle = (estimated: boolean, sample?: number): string => {
  if (sample === undefined) return estimated ? m.measure_latency_estimate() : m.measure_latency_title();
  const inputs = { sample: fmtCallSample(sample) };
  return estimated ? m.measure_latency_estimate_sample(inputs) : m.measure_latency_title_sample(inputs);
};

/**
 * Concept A latency range: an adaptive track (no fixed 10s ceiling,
 * so the marker is never clipped) with a light "typical" band, a filled median
 * marker, and a thin whisker out to the upper value (p99, or p95 when p99 is
 * absent). Visible HTML labels explain each percentile without relying on SVG
 * hover behavior and remain readable when the two values are close together.
 */
export const LatencyRangeChart = ({ range, sample, estimated = false }: LatencyRangeChartProps) => {
  const { typicalMs, upperMs, scaleMs } = range;
  const labels = latencyLabels(range, estimated);
  const x = (ms: number) => latencyTrackX(ms, scaleMs);

  const typicalX = x(typicalMs);
  const upperX = x(upperMs);
  const rangeX = Math.min(typicalX, upperX);
  const rangeWidth = Math.abs(upperX - typicalX);

  const bandX1 = x(typicalMs - 1000);
  const bandW = x(typicalMs + 1000) - bandX1;

  return (
    <div>
      <div className="text-12 font-semibold text-text-2">
        {latencyTitle(estimated, sample)}
      </div>
      <dl className="mt-2 grid grid-cols-1 gap-2 s420:grid-cols-2">
        <div className="grid min-w-0 grid-cols-[1fr_auto] items-baseline gap-x-2 rounded-6 bg-surface-2 px-3 py-2">
          <dt className="text-11 font-semibold text-text-2">{labels.typical}</dt>
          <dd className="mono text-12 font-semibold tabular-nums text-text">
            {labels.typicalValue}
          </dd>
          <dd className="col-span-2 mt-1 text-11 leading-[1.35] text-text-3">
            {labels.typicalMeaning}
          </dd>
        </div>
        <div className="grid min-w-0 grid-cols-[1fr_auto] items-baseline gap-x-2 rounded-6 bg-surface-2 px-3 py-2">
          <dt className="text-11 font-semibold text-text-2">{labels.upper}</dt>
          <dd className="mono text-12 font-semibold tabular-nums text-text">
            {labels.upperValue}
          </dd>
          <dd className="col-span-2 mt-1 text-11 leading-[1.35] text-text-3">
            {labels.upperMeaning}
          </dd>
        </div>
      </dl>
      <svg
        className="mt-2"
        viewBox="0 0 560 42"
        width="100%"
        aria-hidden="true"
      >
        <rect
          x={bandX1}
          y={4}
          width={bandW}
          height={12}
          rx={4}
          fill="var(--accent)"
          fillOpacity={0.16}
          stroke="var(--accent)"
          strokeOpacity={0.6}
          strokeWidth={1}
        />
        <rect x={X0} y={8} width={TRACK} height={4} rx={2} fill="var(--surface-3)" />
        <rect x={rangeX} y={9} width={Math.max(1, rangeWidth)} height={2} rx={1} fill="var(--accent)" />
        <rect x={upperX - 1.2} y={4} width={2.4} height={12} rx={1} fill="var(--accent)" />
        <circle cx={typicalX} cy={10} r={5.5} fill="var(--accent)" />

        <text x={X0} y={34} fontSize="10.5" fill="var(--text-3)" className="mono">
          {fmtSec(0)}
        </text>
        <text x={X0 + TRACK / 2} y={34} textAnchor="middle" fontSize="10.5" fill="var(--text-3)" className="mono">
          {fmtSec(scaleMs / 2)}
        </text>
        <text x={X0 + TRACK} y={34} textAnchor="end" fontSize="10.5" fill="var(--text-3)" className="mono">
          {fmtSec(scaleMs)}
        </text>
      </svg>
    </div>
  );
};
